import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import infrastructureMonitoringService from '../../services/infrastructureMonitoringService';
import CriticalMetricsCard from './components/CriticalMetricsCard';
import ApiPerformancePanel from './components/ApiPerformancePanel';
import DatabaseHealthPanel from './components/DatabaseHealthPanel';
import ClientResourcePanel from './components/ClientResourcePanel';
import AlertsManagementPanel from './components/AlertsManagementPanel';

const InfrastructureMonitoringPerformanceDashboard = () => {
  const [activeTab, setActiveTab] = useState('api_performance');
  const [apiSummary, setApiSummary] = useState(null);
  const [databaseSummary, setDatabaseSummary] = useState(null);
  const [resourceSummary, setResourceSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [timeRange, setTimeRange] = useState('24h');

  const handleNewAlert = (newAlert, eventType) => {
    if (eventType === 'INSERT') {
      setAlerts(prev => [newAlert, ...prev]);
    } else if (eventType === 'UPDATE') {
      setAlerts(prev => prev?.map(alert => alert?.id === newAlert?.id ? newAlert : alert));
    }
  };

  useEffect(() => {
    loadDashboardData();

    // Real-time alert subscription
    const channel = infrastructureMonitoringService?.subscribeToAlerts(handleNewAlert);

    return () => {
      infrastructureMonitoringService?.unsubscribeFromAlerts(channel);
    };
  }, [timeRange]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadDashboardData();
      }, 30000); // Refresh every 30 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, timeRange]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [apiData, dbData, resourceData, alertsData] = await Promise.all([
        infrastructureMonitoringService?.getApiPerformanceSummary(),
        infrastructureMonitoringService?.getDatabasePerformanceSummary(),
        infrastructureMonitoringService?.getClientResourceSummary(),
        infrastructureMonitoringService?.getInfrastructureAlerts({ alertStatus: 'active', limit: 10 })
      ]);

      setApiSummary(apiData);
      setDatabaseSummary(dbData);
      setResourceSummary(resourceData);
      setAlerts(alertsData);
    } catch (err) {
      setError(err?.message || 'Failed to load infrastructure monitoring data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const handleExport = () => {
    const exportData = {
      apiSummary,
      databaseSummary,
      resourceSummary,
      alerts,
      exportedAt: new Date()?.toISOString(),
      timeRange
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `infrastructure-monitoring-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'api_performance', label: 'API Performance', icon: 'Activity' },
    { id: 'database_health', label: 'Database Health', icon: 'Database' },
    { id: 'client_resources', label: 'Client Resources', icon: 'Server' },
    { id: 'alerts', label: 'Alerts', icon: 'AlertTriangle', badge: alerts?.filter(a => a?.alertStatus === 'active')?.length }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Infrastructure Monitoring & Performance Dashboard | PNG HR Analytics</title>
      </Helmet>
      <Header />
      <ComplianceAlertBanner />
      <GlobalFilterBar onFilterChange={setGlobalFilter} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Icon name="Activity" className="w-8 h-8 text-blue-600" />
                Infrastructure Monitoring & Performance Dashboard
              </h1>
              <p className="mt-2 text-gray-600">
                Real-time system health oversight tracking API response times, database performance, and multi-client resource utilization
              </p>
            </div>
            <div className="flex items-center gap-3">
              <DataRefreshIndicator isRefreshing={refreshing} lastRefresh={new Date()} />
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2"
              >
                <Icon name="RefreshCw" className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                onClick={handleExport}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Icon name="Download" className="w-4 h-4" />
                Export
              </Button>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e?.target?.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Auto-refresh (30s)
            </label>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Time Range:</span>
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e?.target?.value)}
              className="rounded-md border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="1h">Last Hour</option>
              <option value="6h">Last 6 Hours</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error Loading Data</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {loading && !apiSummary ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader" className="w-8 h-8 text-blue-600 animate-spin" />
          </div>
        ) : (
          <>
            {/* Critical Metrics Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <CriticalMetricsCard
                title="API Response Time"
                value={`${apiSummary?.avgResponseTime || 0}ms`}
                subtitle={`${apiSummary?.successRate || 0}% success rate`}
                icon="Activity"
                color={parseFloat(apiSummary?.avgResponseTime || 0) < 200 ? 'green' : parseFloat(apiSummary?.avgResponseTime || 0) < 500 ? 'yellow' : 'red'}
                trend={`${apiSummary?.totalRequests || 0} requests`}
              />
              <CriticalMetricsCard
                title="Database Performance"
                value={`${databaseSummary?.avgExecutionTime || 0}ms`}
                subtitle={`${databaseSummary?.avgThroughput || 0} TPS`}
                icon="Database"
                color={parseFloat(databaseSummary?.avgExecutionTime || 0) < 100 ? 'green' : parseFloat(databaseSummary?.avgExecutionTime || 0) < 300 ? 'yellow' : 'red'}
                trend={`${databaseSummary?.poolUtilization || 0}% pool usage`}
              />
              <CriticalMetricsCard
                title="Active Connections"
                value={resourceSummary?.activeConnections || 0}
                subtitle={`${resourceSummary?.cpuUsage || 0}% CPU usage`}
                icon="Users"
                color={parseFloat(resourceSummary?.cpuUsage || 0) < 70 ? 'green' : parseFloat(resourceSummary?.cpuUsage || 0) < 85 ? 'yellow' : 'red'}
                trend={`${resourceSummary?.memoryUsage || 0}% memory`}
              />
              <CriticalMetricsCard
                title="System Resource"
                value={`${resourceSummary?.storageUsage || 0}%`}
                subtitle="Storage utilization"
                icon="HardDrive"
                color={parseFloat(resourceSummary?.storageUsage || 0) < 70 ? 'green' : parseFloat(resourceSummary?.storageUsage || 0) < 85 ? 'yellow' : 'red'}
                trend={resourceSummary?.alertLevel || 'normal'}
              />
            </div>

            {/* Tabbed Monitoring Panels */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Tab Navigation */}
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`
                        relative px-6 py-4 text-sm font-medium border-b-2 transition-colors
                        ${activeTab === tab?.id
                          ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <Icon name={tab?.icon} className="w-4 h-4" />
                        {tab?.label}
                        {tab?.badge > 0 && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {tab?.badge}
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'api_performance' && (
                  <ApiPerformancePanel timeRange={timeRange} />
                )}
                {activeTab === 'database_health' && (
                  <DatabaseHealthPanel timeRange={timeRange} />
                )}
                {activeTab === 'client_resources' && (
                  <ClientResourcePanel timeRange={timeRange} />
                )}
                {activeTab === 'alerts' && (
                  <AlertsManagementPanel alerts={alerts} onRefresh={loadDashboardData} />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InfrastructureMonitoringPerformanceDashboard;